/**
 * The calendar's data: `GET /activity/calendar` for the people shown and the
 * days of the view (§6.2), and *To book* (§6.8). Re-asked whenever the
 * people, the days or Combined change, and on `reload()` after a write.
 */
import { useCallback, useEffect, useState } from 'react'
import type { CalendarAnswer } from '@rowted/shared'
import { activityRequest, peopleQuery } from '../api.js'
import type { ToBookCard } from './side-panel.js'
import { ACTIVITY_MADE_EVENT } from '../new/new-sheets.js'

export function useCalendar(people: string[], from: string, to: string, combined: boolean) {
  const [answer, setAnswer] = useState<CalendarAnswer | null>(null)
  const [toBook, setToBook] = useState<ToBookCard[]>([])
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const key = `${peopleQuery(people)}|${from}|${to}|${combined}`

  useEffect(() => {
    if (people.length === 0) {
      setAnswer(null)
      return
    }
    let live = true
    const query = `/activity/calendar?people=${peopleQuery(people)}&from=${from}&to=${to}${combined ? '&combined=1' : ''}`
    activityRequest<CalendarAnswer>(query)
      .then((value) => { if (live) { setAnswer(value); setError(null) } })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    activityRequest<{ items: ToBookCard[] }>(`/activity/to-book?people=${peopleQuery(people)}`)
      .then((value) => { if (live) setToBook(value.items) })
      .catch(() => { if (live) setToBook([]) })
    return () => { live = false }
  }, [key, tick])

  const reload = useCallback(() => setTick((n) => n + 1), [])
  /* A New sheet made something: the calendar asks again (§9). */
  useEffect(() => {
    window.addEventListener(ACTIVITY_MADE_EVENT, reload)
    return () => window.removeEventListener(ACTIVITY_MADE_EVENT, reload)
  }, [reload])
  return { answer, toBook, error, reload }
}
